import React, { useEffect, useRef, useState  } from 'react';
import { Control } from '@uiw/react-baidu-map';

import './index.module.less';

import { debounce } from 'lodash';
import { ControlAnchor, rem } from '@helpers/Constants';
import Filter from '@helpers/Filter';
import { Api } from '@apis/client';
import Http from '@helpers/Http';
import NativeInterface from '@helpers/NativeInterface';
import { uuid } from '@helpers/utils';
import { isCubeApp, commandMessage, defaultPoint } from '@helpers/Constants';
import FridayService from '@services/friday';
import LogService from '@services/logger';


import NavBar from '@components/NavBar';
import BaseMap  from '@components/map/BaseMap';
import { showLoading, hideLoading } from '@widgets/Loading';
import Toast from '@widgets/Toast';
import Slider from './Slider';
import List from './List';
import SearchList from './SearchList';

const minRadius = 100; //最小半径
const valueArr = [100, 200, 300, 500, 1000];
const maxZoom = 20
const minZoom = 15

const Area = () => {
  const params = useRef(Filter.urlParams(window.location.search));
  const editPosition = useRef(params.current.position ? JSON.parse(decodeURIComponent(params.current.position)) : null);
  const mapRef = useRef(null);
  const [center, setMapCenter] = useState(defaultPoint);
  const [locations, setLocations] = useState(null);
  const [searchLocations, setSearchLocations] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isSearching, setSearchStatus] = useState(false); //是否展开列表
  const [radius, setRadius] = useState(minRadius);
  const timer = useRef(null);
  const [sliderValue, setSliderValue] = useState(0);

  const inputValue = (value: string) => {
    search(value);
  }

  const sortDistanceData = (arr: any[]) => {
    if(arr.length <= 2) {
      return arr;
    }
    const list = arr.sort(function(a: any, b: any){
      return a.distance - b.distance;
    })
    return list;
  }

  //根据输入地点模糊查找位置信息
  const search = debounce((value?: string) => {
    if(!value) {
      setSearchLocations([]);
      hideLoading();
      return;
    }
    clearTimeout(timer.current);
    if(!navigator.onLine) {
      return Toast.show({
        type: 'strip',
        title: '网络异常，请重试'
      })
    }
    showLoading();
    //搜索超时限制6s
    timer.current = setTimeout(() => {
      hideLoading();
      Toast.show({
        type: 'strip',
        title: '网络异常，请重试'
      })
    }, 6000);
    const instanceMap = mapRef.current.getMapRef();
    instanceMap.centerAndZoom(new BMap.Point(center.lng, center.lat), instanceMap.getZoom());
    try {
      const options = {
        onSearchComplete: (results: any) => {
          clearTimeout(timer.current);
          let list = [];
          let listNum = results.getCurrentNumPois();

          if(listNum > 0) {
            for (var i = 0; i < listNum; i ++){
              list.push(results.getPoi(i));
            }
            LogService.log({
              type: 'info',
              name: 'LocalResultPoi success',
              extra: {
                keyword: results.keyword,
                list: list.map(item => JSON.stringify(item))
              }
            });
            list = list.map((item: any, index: any) => {
              return {
                ...item,
                uniqueId: uuid(), //给每个地址结果加个uniqueId
                distance: instanceMap.getDistance(
                  new BMap.Point(center.lng, center.lat),
                  new BMap.Point(item.point.lng, item.point.lat)
                ),
              }
            })
            list = sortDistanceData(list);
          }
          setSearchLocations(list);
          hideLoading();
        }
      };
      const local = new BMap.LocalSearch(instanceMap, options);
      local.search(value);
      local.setPageCapacity(100);
    } catch {
      LogService.log({
        type: 'error',
        message: 'LocalResultPoi fail',
      });
    }
  }, 500)

  //根据当前地址查找附近位置
  const getPois = (centers?: any, r?:number) => {
    const centerData = centers || center;
    const instanceMap = mapRef.current.getMapRef();
    const myGeo = new BMap.Geocoder();
    // 根据坐标得到地址描述
    myGeo.getLocation(new BMap.Point(centerData.lng, centerData.lat), function(result){
      const currentPoint = {
        ...centerData,
        address: result.address || '位置',
        title: result.address,
        distance: 0,
        uniqueId: uuid()
      }
      let list = (result as any)?.surroundingPois  || [];
      list = list.slice(0, 10).map((item: any, index: any) => {
        return {
          ...item,
          uniqueId: uuid(),
          distance: instanceMap.getDistance(
            new BMap.Point(centerData.lng, centerData.lat),
            new BMap.Point(item.point.lng, item.point.lat)
          )
        }
      })
      list.unshift(currentPoint);
      list = sortDistanceData(list);
      setLocations(list);
      setSelectedLocation(list[0]);
    }, { poiRadius: r || radius });
  };

  const confirm = async() => {
    const condition = {
      longitude: selectedLocation.lng || selectedLocation.point.lng,
      latitude: selectedLocation.lat || selectedLocation.point.lat,
      address: encodeURIComponent(selectedLocation.address || selectedLocation.title),
      radius: parseFloat((radius + 0.01).toFixed(2)),
    }
    FridayService.addEvent('security_guard_add_edit_guard_area_regional_location_save_click', { radius: radius + 'm' });
    if(isCubeApp()) {
      NativeInterface.postMessage(commandMessage.closeContainer, {
        requestCode: 1,
        data: condition
      });
    }
  }

  const onSelectItem = (item: any, isSearch?: boolean) => {
    FridayService.addEvent('security_guard_add_edit_guard_area_regional_location_select_address_click');
    setSearchStatus(false);
    setSelectedLocation(item);
    const instanceMap = mapRef.current.getMapRef();
    if(isSearch) {
      setSearchLocations(null);
      const c = { lng: item.lng || item.point.lng, lat: item.lat || item.point.lat };
      instanceMap.setCenter(c);
      getPois(c);
    }
    const centerData = item.lng ? { lng: item.lng, lat: item.lat } : item.point;
    instanceMap.centerAndZoom(new BMap.Point(centerData.lng, centerData.lat), instanceMap.getZoom())
  }

  const onMapLoad = () => {
    if(editPosition.current) {
      setEditPosition();
    } else {
      getPosition();
    }
    const instanceMap = mapRef.current.getMapRef();
    instanceMap.addEventListener('dragend', getTargeDetail);
  }

  //获取移动后的中心点坐标
  const getTargeDetail = (e: any) => {
    FridayService.addEvent('security_guard_add_edit_guard_area_regional_location_drag_the_map');
    const instanceMap = mapRef.current.getMapRef();
    setSelectedLocation(null);
    const p = instanceMap.getCenter();
    const targetPoint = p;
    setMapCenter(targetPoint);
    getPois(targetPoint)
  }

  //设置定位参数（如果是编辑定位地点时）
  const setEditPosition = () => {
    const instanceMap = mapRef.current.getMapRef();
    const editRadius = Math.floor(editPosition.current.radius);
    const idx = valueArr.findIndex(item => item === editRadius);
    const l = 19 - idx;
    const location = {
      lng: editPosition.current.lng,
      lat: editPosition.current.lat
    }
    instanceMap.centerAndZoom(new BMap.Point(location.lng, location.lat), l)
    setMapCenter(location);
    setRadius(editRadius);
    setSliderValue(idx);
    getPois(location);
  }

  //获取设备定位信息
  const getPosition = async () => {
    setSelectedLocation(null);
    const { data } = await Http.post(
      Api.GetCardInfoBySncode,
      { snCode: params.current.sncode, company: params.current.company || 'lingge' }
    )

    if(!data?.isOnLine) {
      Toast.show({
        type: 'strip',
        title: '设备离线，请联网后设置'
      });
    }

    //设备在线时下发实时定位指令
    if(data?.isOnLine) {
      await Http.post(Api.PostPositionV1Realtime, { snCode: params.current.sncode }).catch(e => {});
    }
    //获取定位信息(如果设备有历史定位，则使用历史定位，没有历史定位时使用默认位置为北京市定位
    const { data: result } = await Http.post(Api.GetPositionV1Realtime, { snCode: params.current.sncode });
    let location = defaultPoint;
    if(result) {
      location = { lng: result.longitude, lat: result.latitude };
    }
    const instanceMap = mapRef.current.getMapRef();
    // instanceMap.centerAndZoom(new BMap.Point(location.lng, location.lat), radius);
    instanceMap.setCenter(new BMap.Point(location.lng, location.lat));
    setMapCenter(location);
    getPois(location);
    FridayService.addEvent('security_guard_add_edit_guard_area_regional_location_locate_the_device_location_click');
  }

  const cancelSearch = () => {
    setSearchStatus(false);
    setSearchLocations([]);
  }

  const controlSearchList = () => {
    FridayService.addEvent('security_guard_add_edit_guard_area_regional_location_search_click');
    setSearchStatus(true);
  }

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const instanceMap = mapRef.current.getMapRef();
      if(instanceMap) {
        instanceMap.removeEventListener('dragend', getTargeDetail);
      }
    }
    // eslint-disable-next-line
  }, [])

  const onChangeSlider = (value: any) => {
    FridayService.addEvent('security_guard_add_modify_guard_area_guard_range_drag_click');
    const instanceMap = mapRef.current.getMapRef();
    if(value > sliderValue) {
      const targetZoom = instanceMap.getZoom() - (value - sliderValue);
      instanceMap.setZoom(targetZoom);
    }else {
      const targetZoom = instanceMap.getZoom() + (sliderValue - value);
      instanceMap.setZoom(targetZoom);
    }
    const targetRadius = valueArr[value];
    setSliderValue(value);
    setRadius(targetRadius);
    getPois(null, targetRadius);
  }

  const radiusText = radius >= 1000 ? (radius / 1000).toFixed(0) + 'km' : radius + 'm';
  return (
    <div style={{ width: '100%', height: document.body.clientHeight * 0.65 }}>
      <NavBar
        title=" "
        transparent
        RightBtnComponent={(
          <div
            styleName="nav-right-btn"
            className="flex flex-center"
            style={{ background: '#256EFF'}}
            onClick={confirm}
          >
            保存
          </div>
        )}
      />
      <BaseMap
        ref={mapRef}
        onMapLoad={onMapLoad}
        zoom={19}
        maxZoom={ maxZoom }
        minZoom={ minZoom }
        enablePinchToZoom={ false }
        enableDoubleClickZoom={false}
      >
        <Control
          anchor={ ControlAnchor.BMAP_ANCHOR_TOP_RIGHT }
          offset={{ width: 19, height: document.body.clientHeight * 0.45 }}
        >
          <div
            className="flex flex-center"
            styleName="icon-genlocation"
            onClick={ getPosition }
          >
            <i className="iconfont icon-dingwei" />
          </div>
        </Control>
        <Control
          anchor={ ControlAnchor.BMAP_ANCHOR_TOP_RIGHT }
          offset={{ width: 19, height: document.body.clientHeight * 0.525 }}
        >
          <div
            className="flex"
            styleName="slider-wrapper"
          >
            <div>守护范围</div>
            <div styleName="slider">
              <Slider
                max={ valueArr.length - 1 }
                min={ 0 }
                step={ 1 }
                value={ sliderValue }
                markStep={ 1 }
                onChange={ onChangeSlider }
                showMarks={ true }
              />
            </div>
          </div>
        </Control>

        <Control
          anchor={ ControlAnchor.BMAP_ANCHOR_TOP_LEFT }
          offset={{ width: (document.body.clientWidth -  5 * rem) / 2, height: document.body.clientHeight * 0.12 }}
        >
          <div
            className="flex flex-center"
            styleName="map-circle"
          >
            <div styleName="map-marker">
            </div>
            <div styleName="map-line">
              <div styleName="map-line-text">{ radiusText }</div>
            </div>
          </div>
        </Control>
      </BaseMap>

      <List
        items={ locations }
        onShowSearch={ controlSearchList }
        onSelectItem={ onSelectItem }
        selectedLocation={ selectedLocation }
      />
      {
        isSearching ? (
          <SearchList
            items={ searchLocations }
            onSelectItem={ onSelectItem }
            selectedLocation={ selectedLocation }
            onCancel={ cancelSearch }
            onSearch={ inputValue }
          />
        ) : null
      }
    </div>
  )
}
export default Area;